import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Download, FileSpreadsheet, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { CollapsibleSection } from "@/components/settings/SettingsUI";
import { getFoundationBudgetRegister, downloadFoundationBudgetCsv } from "@/lib/api";

const fmt = (s) => (s ? String(s).replace("T", " ").slice(0, 16) : "—");

const COMPONENT_LABELS = { material: "material cost", freight: "freight cost", labor: "labor hours × cost", labor_hours: "labor hours",
  equipment: "equipment", travel: "travel", disposal: "disposal", drive_time: "drive time cost", drive_time_hours: "drive time hours", subcontract: "subcontract cost" };
const costSourceTitle = (r) => Object.entries(r.components || {})
  .filter(([, v]) => parseFloat(v) !== 0)
  .map(([k, v]) => `${COMPONENT_LABELS[k] || k}: ${v}`)
  .concat(r.source?.notes || [])
  .join("\n") || "No cost components";

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.setAttribute("data-testid", "budget-csv-download-anchor");
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

// Jobs pushed to Foundation → Genie budget import CSV. File download only:
// no bridge call, no Foundation write. Values are the WO's cost breakdown
// at export time; accounting imports the file and decides on re-imports.
export default function FoundationBudgetRegister({ onCountChange }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(null);

  const reload = useCallback(async (query) => {
    setLoading(true);
    try {
      const { data: d } = await getFoundationBudgetRegister({ q: query || undefined, limit: 200 });
      setData(d);
      onCountChange?.(d.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the pushed-jobs register");
    } finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => {
    const t = setTimeout(() => reload(q), q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, reload]);

  // A successful job push elsewhere on the page lands here immediately.
  useEffect(() => {
    const h = () => reload(q);
    window.addEventListener("foundation:job-pushed", h);
    return () => window.removeEventListener("foundation:job-pushed", h);
  }, [q, reload]);

  const download = async (woIds, key) => {
    setBusy(key);
    try {
      const res = await downloadFoundationBudgetCsv(woIds);
      const filename = res.headers?.["x-budget-export-filename"]
        || (res.headers?.["content-disposition"] || "").match(/filename="?([^";]+)"?/)?.[1]
        || "Foundation_Budget.csv";
      saveBlob(res.data, filename);
      const flagged = rows.filter((r) => woIds.includes(r.wo_id) && r.flags?.length).map((r) => r.job_no);
      const desc = ["CO costs are sent via the change order push. USE $ → Foundation cost class Use Tax (10) (Genie mapping).",
        flagged.length ? `Exported anyway with flags: ${flagged.join(", ")} — accounting decides whether to import or hand-enter.` : null].filter(Boolean).join(" ");
      toast.success(`${filename} downloaded — ${woIds.length} job${woIds.length === 1 ? "" : "s"} logged`, { description: desc });
      setSelected(new Set());
      reload(q);
    } catch (e) {
      let msg = "Download failed";
      try { msg = JSON.parse(await e?.response?.data?.text?.())?.detail || msg; } catch { /* blob without json */ }
      toast.error(msg);
    } finally { setBusy(null); }
  };

  const rows = data?.rows || [];
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.wo_id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.wo_id)));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <CollapsibleSection
      id="foundation-budget-register" testId="foundation-budget-register"
      icon={FileSpreadsheet} iconClass="text-indigo-600"
      title={`Jobs pushed to Foundation — budget CSV (${data ? data.total : "…"})`}
      actions={
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Job #, title, customer"
                   className="pl-6 pr-2 py-1 text-xs border border-slate-300 rounded-md w-44" data-testid="budget-register-search" />
          </div>
          <button type="button" onClick={() => reload(q)} disabled={loading} className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid="budget-register-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      }
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="text-[11px] text-slate-500">
          Export each job's budget <strong>before</strong> it gets a completion date — Foundation rejects budget imports on completed jobs. Genie import file: one row per job, cost code <code>{data?.cost_code || "20-100000"}</code>, columns {data?.columns?.join(" · ")}. No header, CRLF, two decimals. <strong>All columns are at cost</strong>, read live from the linked proposal scopes (never sell prices) — <strong>original scope only; CO costs are sent via the change order push.</strong> Columns: MAT = material cost × qty · FRE = raw freight · LAB = labor hours × labor cost (drive time included) · REQ = equipment · OTH = disposal + travel · SUB = subcontract cost · USE $ = jurisdiction rate × material cost, freight excluded, even when tax is hidden on customer documents — <strong>use tax is Foundation cost class Use Tax (10)</strong> (accounting's Genie column mapping maps USE $ → Use Tax (10); class 7 "Other" is drive time / disposal only). Jobs without a proposal export labor only. Nothing is written to Foundation — accounting imports the file.
        </div>
        <button type="button" disabled={selected.size === 0 || busy !== null} onClick={() => download(rows.filter((r) => selected.has(r.wo_id)).map((r) => r.wo_id), "multi")}
                className="h-7 px-2.5 text-[11px] rounded-md bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="budget-download-selected">
          <Download className="w-3 h-3" /> Download Foundation budget CSV ({selected.size})
        </button>
      </div>
      {data && rows.length === 0 && <div className="text-xs text-slate-400 py-3" data-testid="budget-register-empty">No jobs have been pushed to Foundation yet.</div>}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-[11px]" data-testid="budget-register-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-2 py-1"><input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Select All" data-testid="budget-select-all" /></th>
                <th className="text-left px-2 py-1">Job</th>
                <th className="text-left px-2 py-1">Title / customer</th>
                {(data.columns || []).map((c) => <th key={c} className="text-right px-2 py-1 whitespace-nowrap">{c}</th>)}
                <th className="text-left px-2 py-1">Budget Exported</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.wo_id} className="border-t border-slate-100" data-testid={`budget-row-${r.wo_id}`}>
                  <td className="px-2 py-1"><input type="checkbox" checked={selected.has(r.wo_id)} onChange={() => toggle(r.wo_id)} data-testid={`budget-select-${r.wo_id}`} /></td>
                  <td className="px-2 py-1 whitespace-nowrap">
                    <div className="font-mono font-semibold">{r.job_no}{r.hide_tax && <span className="ml-1 text-[9px] font-sans font-normal text-slate-500" title="Tax hidden on customer documents — USE $ still carries the real tax">hide-tax</span>}</div>
                    <button type="button" disabled={busy !== null} onClick={() => download([r.wo_id], r.wo_id)}
                            className="mt-0.5 h-5 px-1.5 text-[10px] rounded border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid={`budget-download-${r.wo_id}`}>
                      <Download className="w-3 h-3" /> CSV
                    </button>
                  </td>
                  <td className="px-2 py-1 text-slate-600 max-w-[16rem] truncate" title={r.title}>{r.title}<div className="text-[10px] text-slate-400 truncate">{r.customer_name}</div></td>
                  {["REQ", "FRE", "LAB", "LAB_UNITS", "MAT", "OTH", "SUB", "USE"].map((c) => (
                    <td key={c} className="px-2 py-1 text-right font-mono whitespace-nowrap" data-testid={`budget-val-${r.wo_id}-${c}`}>{r.values[c]}</td>
                  ))}
                  <td className="px-2 py-1 max-w-[20rem]">
                    {r.exported ? (
                      <span className="flex flex-col gap-0.5" data-testid={`budget-stamp-${r.wo_id}`}>
                        <span><span className="text-emerald-700">{fmt(r.exported.at)}</span> <span className="text-slate-400">{r.exported.by}</span></span>
                        {r.delta && (
                          <span className="inline-flex items-start gap-0.5 text-amber-800 whitespace-normal" title={r.delta.map((d) => `${d.column}: ${d.previous} → ${d.current}`).join("\n")} data-testid={`budget-delta-${r.wo_id}`}>
                            <AlertTriangle className="w-3 h-3" /> changed: {r.delta.map((d) => `${d.column} ${d.previous}→${d.current}`).join(", ")}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200"
                            title="Export and import this budget before the job gets a completion date — Foundation rejects budget imports on completed jobs"
                            data-testid={`budget-stamp-${r.wo_id}`}>
                        <AlertTriangle className="w-3 h-3" /> Budget not exported
                      </span>
                    )}
                    {r.flags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`budget-flags-${r.wo_id}`}>
                        {r.flags.map((f, i) => (
                          <span key={i} className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${f.startsWith("Sub cost") ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-300"}`}
                                title={f} data-testid={`budget-flag-${r.wo_id}-${i}`}>
                            <AlertTriangle className="w-3 h-3" /> {f}
                          </span>
                        ))}
                      </div>
                    )}
                    {r.source && (
                      <div className="text-[10px] text-slate-400 truncate" title={costSourceTitle(r)} data-testid={`budget-source-${r.wo_id}`}>
                        {r.mode === "proposal" ? `cost from proposal${r.source.proposal_name ? ` “${r.source.proposal_name}”` : ""}` : `labor @ $${r.source.labor_cost_rate}/hr × hours`}
                        {r.change_orders_excluded?.length > 0 && ` · CO ${r.change_orders_excluded.join(", ")} excluded (sent via change order push)`}
                        {` · tax ${r.tax_rate ?? 0}% × MAT`}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleSection>
  );
}
